// src/ui/screens/settings/QrDisplayScreen.jsx

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { userProfileService } from '../../../services/user/userProfileService';

const COLORS = {
  orange: '#FF8C08',
  bg: '#08090B',
  card: '#23262C',
  cardLight: '#2C2F36',
};

// ── QR Code Generator (using quickchart.io) ────────────────────────────────

const generateQRCode = async (text) => {
  const qrData = encodeURIComponent(text);
  const apiUrl = `https://quickchart.io/qr?text=${qrData}&size=400&margin=2&ecLevel=H`;
  const response = await fetch(apiUrl);
  if (!response.ok) {
    throw new Error('Failed to generate QR code');
  }
  const blob = await response.blob();
  return URL.createObjectURL(blob);
};

// ── Action Button ───────────────────────────────────────────────────────────

const ActionButton = ({ icon, label, onTap, color }) => {
  const getIconPath = () => {
    switch (icon) {
      case 'share':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z" />
        );
      case 'link':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
        );
      case 'qr_code':
        return (
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
        );
      default:
        return null;
    }
  };

  return (
    <button
      onClick={onTap}
      className="flex-1 py-3 rounded-xl flex flex-col items-center gap-1.5 border border-white/10"
      style={{ backgroundColor: COLORS.card }}
    >
      <svg className="w-6 h-6" fill="none" stroke={color} viewBox="0 0 24 24">
        {getIconPath()}
      </svg>
      <span className="text-white/70 text-xs font-medium">{label}</span>
    </button>
  );
};

// ── QrDisplayScreen ─────────────────────────────────────────────────────────

export const QrDisplayScreen = () => {
  const navigate = useNavigate();
  const [profile, setProfile] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [qrImageUrl, setQrImageUrl] = useState(null);
  const [isGeneratingQr, setIsGeneratingQr] = useState(false);

  // ── Load profile ──────────────────────────────────────────────────────────

  useEffect(() => {
    const loadProfile = async () => {
      try {
        const cached = userProfileService.cachedProfile;
        if (cached) {
          setProfile(cached);
          setIsLoading(false);
          return;
        }
        const profileData = await userProfileService.getCurrentUserProfile();
        if (profileData) {
          setProfile(profileData);
        }
      } catch (error) {
        console.error('Load profile error:', error);
      } finally {
        setIsLoading(false);
      }
    };
    loadProfile();
  }, []);

  // ── Generate QR code ──────────────────────────────────────────────────────

  useEffect(() => {
    const generateQr = async () => {
      if (!profile?.uid) return;
      setIsGeneratingQr(true);
      try {
        const profileUrl = `https://filatom.com/profile/${profile.uid}`;
        const qrUrl = await generateQRCode(profileUrl);
        setQrImageUrl(qrUrl);
      } catch (error) {
        console.error('QR generation error:', error);
      } finally {
        setIsGeneratingQr(false);
      }
    };
    generateQr();
  }, [profile]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleBack = () => {
    navigate(-1);
  };

  const handleShareLink = async () => {
    const url = `https://filatom.com/profile/${profile?.uid || ''}`;
    try {
      if (navigator.share) {
        await navigator.share({
          title: 'My Filatom Profile',
          text: `Check out my profile on Filatom!\n${url}`,
        });
      } else {
        await navigator.clipboard.writeText(url);
        alert('Profile link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share error:', error);
    }
  };

  const handleCopyLink = async () => {
    const url = `https://filatom.com/profile/${profile?.uid || ''}`;
    try {
      await navigator.clipboard.writeText(url);
      alert('Profile link copied to clipboard!');
    } catch (error) {
      console.error('Copy error:', error);
    }
  };

  const handleShareQr = async () => {
    if (!qrImageUrl) {
      alert('Generating QR code...');
      return;
    }
    try {
      if (navigator.share) {
        const response = await fetch(qrImageUrl);
        const blob = await response.blob();
        const file = new File([blob], 'filatom_qr.png', { type: 'image/png' });
        await navigator.share({
          title: 'My Filatom Profile QR Code',
          files: [file],
        });
      } else {
        const url = `https://filatom.com/profile/${profile?.uid || ''}`;
        await navigator.clipboard.writeText(
          `Scan this QR code to view my profile on Filatom!\n\n${url}`
        );
        alert('Profile link copied to clipboard!');
      }
    } catch (error) {
      console.error('Share QR error:', error);
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const profileUrl = `https://filatom.com/profile/${profile?.uid || ''}`;
  const displayName = profile?.displayName || 'Filatom User';
  const username = profile?.username ? `@${profile.username}` : '@user';
  const hasProfileImage = profile?.hasProfileImage || false;
  const profileImage = profile?.profileImage || '';

  // Shared QR card — used by both layouts, parameterized by size so the
  // desktop version can render a bit larger without duplicating the
  // avatar/QR/user-info markup.
  const QrCard = ({ avatarSize = 60, qrSize = 220 }) => (
    <div className="p-6 rounded-2xl flex flex-col items-center" style={{ backgroundColor: '#FFFFFF' }}>
      {/* Profile Avatar */}
      <div
        className="rounded-full flex items-center justify-center border-2 overflow-hidden flex-shrink-0"
        style={{ width: avatarSize, height: avatarSize, borderColor: COLORS.orange }}
      >
        {hasProfileImage ? (
          <img
            src={profileImage}
            alt="Profile"
            className="w-full h-full rounded-full object-cover"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
        ) : (
          <svg className="w-[30px] h-[30px] text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
          </svg>
        )}
      </div>

      <div className="h-4" />

      {/* QR Code Image */}
      <div className="rounded-xl overflow-hidden bg-white" style={{ width: qrSize, height: qrSize }}>
        {isGeneratingQr || isLoading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-8 h-8 border-2 border-filatom-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : qrImageUrl ? (
          <img src={qrImageUrl} alt="QR Code" className="w-full h-full object-contain" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <svg className="w-16 h-16 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 4v1m6 11h2m-6 0h-2v4m0-11v3m0 0h.01M12 12h4.01M16 20h4M4 12h4m12 0h.01M5 8h2a1 1 0 001-1V5a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1zm12 0h2a1 1 0 001-1V5a1 1 0 00-1-1h-2a1 1 0 00-1 1v2a1 1 0 001 1zM5 20h2a1 1 0 001-1v-2a1 1 0 00-1-1H5a1 1 0 00-1 1v2a1 1 0 001 1z" />
            </svg>
          </div>
        )}
      </div>

      <div className="h-4" />

      {/* User Info */}
      <p className="text-black text-sm font-semibold">{username}</p>
      <p className="text-black/50 text-xs">{displayName}</p>
    </div>
  );

  const ActionButtonsRow = () => (
    <div className="flex gap-3">
      <ActionButton icon="share" label="Share Link" onTap={handleShareLink} color={COLORS.orange} />
      <ActionButton icon="link" label="Copy Link" onTap={handleCopyLink} color="#4285F4" />
      <ActionButton icon="qr_code" label="Share QR" onTap={handleShareQr} color="#34A853" />
    </div>
  );

  const HowToUseCard = () => (
    <div className="p-4 rounded-xl" style={{ backgroundColor: COLORS.card }}>
      <div className="flex items-center gap-3">
        <div
          className="w-10 h-10 rounded-xl flex items-center justify-center"
          style={{ backgroundColor: `${COLORS.orange}1F` }}
        >
          <svg className="w-5 h-5 text-filatom-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <span className="text-white text-sm font-semibold">How to use</span>
      </div>
      <div className="mt-3 ml-13 space-y-1.5">
        <p className="text-white/70 text-xs">• Share your profile link with friends</p>
        <p className="text-white/70 text-xs">• They can open it in any browser</p>
        <p className="text-white/70 text-xs">• Or share the QR code image</p>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-filatom-bg">
      {/* ══════════════════════════════════════════════════════════════════
          MOBILE LAYOUT (unchanged) — visible below the md breakpoint
         ══════════════════════════════════════════════════════════════════ */}
      <div className="md:hidden">
        {/* Top Bar */}
        <div className="pt-safe-top px-3 pb-1">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10"
              style={{ backgroundColor: COLORS.card }}
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <span className="text-white text-xl font-bold">My QR Code</span>
          </div>
        </div>

        {/* Content */}
        <div className="px-4 pb-8">
          <div className="mt-2 text-center">
            <p className="text-white text-lg font-bold tracking-wide">
              SCAN TO SUBSCRIBE TO MY CHANNEL
            </p>
          </div>

          <div className="mt-6">
            <QrCard />
          </div>

          <div className="mt-6">
            <ActionButtonsRow />
          </div>

          <div className="mt-6">
            <HowToUseCard />
          </div>
        </div>
      </div>

      {/* ══════════════════════════════════════════════════════════════════
          DESKTOP LAYOUT (new) — visible at md breakpoint and up.
          Sticky header, then a single centered column — the QR card is
          the whole point of this screen, so it stays the focal point
          rather than being split into a grid.
         ══════════════════════════════════════════════════════════════════ */}
      <div className="hidden md:block">
        <div className="sticky top-0 z-10 backdrop-blur-sm border-b border-white/10" style={{ backgroundColor: `${COLORS.bg}E6` }}>
          <div className="max-w-5xl mx-auto px-8 h-20 flex items-center gap-5">
            <button
              onClick={handleBack}
              className="w-10 h-10 rounded-full flex items-center justify-center border border-white/10 hover:bg-white/10 transition-colors"
              style={{ backgroundColor: COLORS.card }}
              aria-label="Go back"
            >
              <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <div>
              <h1 className="text-white text-2xl font-bold leading-tight">My QR Code</h1>
              <p className="text-white/40 text-sm mt-0.5">Scan to subscribe to my channel</p>
            </div>
          </div>
        </div>

        <div className="max-w-md mx-auto px-8 py-12 flex flex-col items-center">
          <QrCard avatarSize={72} qrSize={260} />

          <div className="w-full mt-6">
            <ActionButtonsRow />
          </div>

          <div className="w-full mt-6">
            <HowToUseCard />
          </div>
        </div>
      </div>
    </div>
  );
};

export default QrDisplayScreen;